import { MessageResponse, MessageSender, MessageType } from '../types';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface MessageItemProps {
  message: MessageResponse;
}

export default function MessageItem({ message }: MessageItemProps) {
  const formatTime = (date: string) => {
    return format(new Date(date), 'HH:mm');
  };

  // System message notification
  if (message.message_type === MessageType.SYSTEM_MESSAGE) {
    return (
      <div className="group flex justify-start mb-4" aria-live="polite">
        <div className="max-w-[70%] flex flex-col items-start">
          <div className="relative bg-gray-100 rounded-lg px-4 py-2 border-l-4 border-orange-500">
            <div className="flex items-center gap-2 text-xs font-medium text-orange-700 mb-1">
              <div className="w-1.5 h-1.5 bg-orange-500 rounded-full"></div>
              <span>System Alert</span>
            </div>
            <div className="text-sm text-gray-600 whitespace-pre-wrap break-words max-w-none">
              {message.content}
            </div>
          </div>
          <div className="text-xs text-gray-400 mt-1 px-1">
            {formatTime(message.created_at)}
          </div>
        </div>
      </div>
    );
  }

  // Session dividers (SESSION_START, SESSION_END)
  if (
    message.message_type === MessageType.SESSION_START ||
    message.message_type === MessageType.SESSION_END
  ) {
    return (
      <div className="flex items-center justify-center py-3">
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <div className="h-px bg-gray-200 flex-1 w-[100px]" />
          <span className="flex items-center gap-1">
            {message.message_type === MessageType.SESSION_START
              ? 'Session started • '
              : 'Session ended • '}
            {formatTime(message.created_at)}
          </span>
          <div className="h-px bg-gray-200 flex-1 w-[100px]" />
        </div>
      </div>
    );
  }

  // Flagged conversation warning
  if (message.message_type === MessageType.CHAT_FLAGGED) {
    return (
      <div className="flex items-center justify-center py-2">
        <span className="inline-flex items-center gap-2 rounded-full border border-red-300 bg-red-50 px-3 py-1 text-xs text-red-600 shadow-sm">
          <span>❗</span>
          <span>Flagged: {message.content}</span>
        </span>
      </div>
    );
  }

  // Chat bubbles (USER_MESSAGE, AGENT_RESPONSE)
  const isUser = message.sender === MessageSender.USER;

  return (
    <div
      className={`group flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}
      aria-live="polite"
    >
      <div
        className={`max-w-[70%] ${isUser ? 'items-end' : 'items-start'} flex flex-col`}
      >
        <div
          className={`
            relative rounded-lg px-4 py-2 transition-colors duration-200 select-text
            ${
              isUser
                ? 'bg-[#c31330] text-white ml-auto'
                : 'bg-gray-100 text-gray-900'
            }
          `}
        >
          {isUser ? (
            <div className="text-sm whitespace-pre-wrap break-words max-w-none">
              {message.content}
            </div>
          ) : (
            <div className="text-sm prose prose-sm max-w-none prose-p:my-2 prose-p:text-gray-700 prose-ul:my-2 prose-ol:my-2 prose-li:my-1 prose-li:text-gray-700 prose-strong:text-gray-900 prose-a:text-[#c31330] prose-a:font-normal prose-a:no-underline prose-a:hover:underline prose-a:break-words prose-headings:text-gray-800 prose-headings:font-semibold prose-headings:mt-4 prose-headings:mb-2 prose-h1:text-[18px] prose-h2:text-[16px] prose-h3:text-[15px] prose-h4:text-[14px] prose-h5:text-[13px] prose-h6:text-[12px]">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  a: ({ href, children }) => (
                    <a href={href} target="_blank" rel="noopener noreferrer">
                      {children}
                    </a>
                  ),
                }}
              >
                {message.content}
              </ReactMarkdown>
            </div>
          )}
        </div>
        <div className="text-xs text-gray-400 mt-1 px-1">
          {formatTime(message.created_at)}
        </div>
      </div>
    </div>
  );
}
